import { FC } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { FeatureFlagKey } from '@libs/constants';

import { AppLayout, AssessmentLayout, AuthLayout, FeatureGuard, GlobalLoading } from '../components';
import { DI_TOKENS } from '../constants';
import {
  AdminDashboardPage,
  AdminDemoRequestsPage,
  AdminFeaturesPage,
  AdminGuard,
  AdminLayout,
  AdminTenantDetailPage,
  AdminTenantsPage,
  AdminUsagePage,
  AdminUsersPage,
} from '../features/admin';
import { AssessmentProgressPage } from '../features/assessment';
import {
  RemediationListPage,
  RemediationPlaceholdersPage,
  RemediationProgressPage,
  RemediationReviewPage,
  RemediationScopePage,
  RemediationSummaryPage,
} from '../features/remediation';
import {
  DocumentReviewPage,
  OrganizationProfilePage,
  TenantProfileHubPage,
  TenantProfileUploadLayout,
} from '../features/tenant-profile-hub';
import { useInjection } from '../hooks';
import { AuthStore } from '../stores';

import {
  AssessmentOverviewPage,
  Assessments,
  AssessmentSummaryPage,
  AssessmentWorkflowPage,
  ChatPage,
  DashboardPage,
  HomePage,
  Login,
  NewAssessmentPage,
  NewChatPage,
  Settings,
} from './pages';
import { routes } from './routes';

// Public router (unauthenticated)
const authRouter = createBrowserRouter([
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      { index: true, element: <Navigate to="/login" replace /> },
      { path: '/login', element: <Login /> },
      { path: '/home', element: <HomePage /> },
      { path: '*', element: <Navigate to="/login" replace /> },
    ],
  },
]);

/**
 * Default landing for an authenticated user. Platform admins are taken straight
 * to the admin console — they may also belong to an Organization, but the admin
 * panel is their home base, so we don't drop them on the tenant dashboard.
 * Everyone else lands on the normal dashboard.
 *
 * This also handles the post-login redirect: after login sets the token the
 * router swaps auth→app with the URL still at /login, which no app route matches,
 * so it falls through to the catch-all below.
 */
const LandingRedirect: FC = observer(() => {
  const authStore = useInjection<AuthStore>(DI_TOKENS.AuthStore);
  const to = authStore.userProfile?.isPlatformAdmin
    ? routes.adminDashboard()
    : routes.dashboard();

  return <Navigate to={to} replace />;
});

// Private router (authenticated)
const appRouter = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    children: [
      { index: true, element: <LandingRedirect /> },
      { path: 'dashboard', element: <DashboardPage /> },
      {
        // The whole remediation flow lives here rather than under /assessments:
        // it is its own feature, and a run outlives the assessment version it
        // started from (finalize forks a new one).
        //
        // `runs` is a literal segment declared before `:assessmentId`, so a run
        // url can never be swallowed by the assessment param.
        //
        // Gated by the runtime `remediation` feature flag, flippable by a
        // platform admin from /admin/features. The backend independently 404s
        // every remediation route when the flag is off; this just keeps a
        // disabled tenant from ever reaching a route that would 404 anyway.
        path: 'remediation',
        element: (
          <FeatureGuard feature={FeatureFlagKey.REMEDIATION}>
            <Outlet />
          </FeatureGuard>
        ),
        children: [
          { index: true, element: <RemediationListPage /> },
          { path: 'runs/:runId', element: <RemediationProgressPage /> },
          { path: 'runs/:runId/review', element: <RemediationReviewPage /> },
          { path: 'runs/:runId/placeholders', element: <RemediationPlaceholdersPage /> },
          { path: 'runs/:runId/summary', element: <RemediationSummaryPage /> },
          { path: ':assessmentId/scope', element: <RemediationScopePage /> },
        ],
      },
      {
        path: 'chat',
        children: [
          { index: true, element: <NewChatPage /> },
          {
            path: ':conversationId',
            Component: () => {
              const conversationId = useParams<{ conversationId: string }>().conversationId;

              return conversationId ? (
                <ChatPage conversationId={conversationId} key={conversationId} />
              ) : null;
            },
          },
        ],
      },
      { path: 'settings', element: <Settings /> },
      {
        path: 'assessments',
        children: [
          { index: true, element: <Assessments /> },
          { path: 'new', element: <NewAssessmentPage /> },
          {
            path: ':assessmentId/summary',
            element: <AssessmentSummaryPage />,
          },
          {
            // Before the bare `:assessmentId` layout below: both would match
            // `/assessments/<id>/progress`, and this one is the specific case.
            //
            // Flag-gated so the screen can ship dark. `useSubmitAssessment`
            // checks the same flag before sending anyone here — without that,
            // submitting with the flag off would land on a route that
            // immediately bounces to the dashboard.
            path: ':assessmentId/progress',
            element: (
              <FeatureGuard feature={FeatureFlagKey.ASSESSMENT_PROGRESS}>
                <AssessmentProgressPage />
              </FeatureGuard>
            ),
          },
          {
            path: ':assessmentId',
            element: <AssessmentLayout />,
            children: [
              {
                path: 'domain/:domainId',
                element: <AssessmentWorkflowPage />,
              },
            ],
          },
          {
            path: ':assessmentId/overview',
            element: <AssessmentOverviewPage />,
          },
        ],
      },
      {
        path: 'tenant-profile',
        children: [
          // The profile + hub pages share a whole-page drag-and-drop upload
          // layout; the focused review page deliberately sits outside it.
          {
            element: <TenantProfileUploadLayout />,
            children: [
              { index: true, element: <OrganizationProfilePage /> },
              { path: 'document-hub', element: <TenantProfileHubPage /> },
            ],
          },
          {
            path: 'document-hub/:tenantProfileDocumentId/review',
            element: <DocumentReviewPage />,
          },
        ],
      },
      { path: '*', element: <LandingRedirect /> },
    ],
  },
  // Platform super-admin panel — its own full-screen shell (own header + nav),
  // gated by the platform_admin realm role. A top-level sibling of AppLayout.
  {
    path: '/admin',
    element: (
      <AdminGuard>
        <AdminLayout />
      </AdminGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/admin/dashboard" replace /> },
      { path: 'dashboard', element: <AdminDashboardPage /> },
      { path: 'organizations', element: <AdminTenantsPage /> },
      { path: 'organizations/:tenantId', element: <AdminTenantDetailPage /> },
      { path: 'users', element: <AdminUsersPage /> },
      { path: 'demo-requests', element: <AdminDemoRequestsPage /> },
      { path: 'features', element: <AdminFeaturesPage /> },
      { path: 'usage', element: <AdminUsagePage /> },
    ],
  },
]);

export const AppRouter: FC = observer(() => {
  const authStore = useInjection<AuthStore>(DI_TOKENS.AuthStore);

  if (authStore.isLoading) {
    return <GlobalLoading />;
  }

  if (!authStore.isLoggedIn) {
    return <RouterProvider router={authRouter} key="auth" />;
  }

  return <RouterProvider router={appRouter} key="app" />;
});
